// ============================================================
// DOCTOR INVOICES PAGE — Flow 1 (monthly τιμολόγιο DocNow -> doctor)
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Card, PageHeader, ActionBtn } from '../components/Shared';
import { doctorInvoicesAPI } from '../services/api';

const MONTHS_EL = [
  'Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος',
  'Μάιος','Ιούνιος','Ιούλιος','Αύγουστος',
  'Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος',
];

const fmtEuro = (cents) => {
  if (cents == null) return '—';
  return new Intl.NumberFormat('el-GR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(cents) / 100);
};

const StatusBadge = ({ status, count }) => {
  if (count === 0) {
    return <span style={{ color: 'var(--muted)', fontSize: 12 }}>Παράλειψη</span>;
  }
  const config = {
    pending:  { label: 'Εκκρεμεί',  bg: '#FEF3C7', fg: '#92400E' },
    issued:   { label: 'Εκδόθηκε',  bg: '#D1FAE5', fg: '#065F46' },
    failed:   { label: 'Απέτυχε',   bg: '#FEE2E2', fg: '#991B1B' },
    cancelled:{ label: 'Ακυρωμένο', bg: '#F3F4F6', fg: '#374151' },
  };
  const c = config[status] || { label: '—', bg: '#F3F4F6', fg: '#374151' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.fg, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
};

export default function DoctorInvoicesPage() {
  const now = new Date();
  // Default to previous month (invoices are typically issued after month-end)
  const defaultDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const [year, setYear]   = useState(defaultDate.getFullYear());
  const [month, setMonth] = useState(defaultDate.getMonth() + 1);
  const [rows, setRows]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [issuingId, setIssuingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await doctorInvoicesAPI.listMonth(year, month);
      setRows(data?.data?.doctors || []);
    } catch (err) {
      console.error('load doctor invoices:', err);
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const handleIssue = async (row) => {
    if (issuingId) return;
    // Basic client-side guards mirror the backend
    if (!row.tax_id) {
      toast.error('Ο ιατρός δεν έχει ΑΦΜ. Συμπληρώστε τα στοιχεία τιμολόγησης.');
      return;
    }
    if (!row.address_line || !row.city || !row.postal_code) {
      toast.error('Ο ιατρός δεν έχει πλήρη διεύθυνση.');
      return;
    }
    if (!row.consult_count || row.consult_count === 0) {
      toast.error('Ο ιατρός δεν έχει τιμολογήσιμες συνεδρίες αυτόν τον μήνα.');
      return;
    }

    const confirmed = window.confirm(
      `Έκδοση τιμολογίου προμήθειας:\n\n` +
      `Ιατρός: ${row.first_name} ${row.last_name}\n` +
      `Περίοδος: ${MONTHS_EL[month - 1]} ${year}\n` +
      `Συνεδρίες: ${row.consult_count}\n` +
      `Σύνολο: ${fmtEuro(row.gross_cents)}\n\n` +
      `Θα υποβληθεί στο myDATA μέσω Elorus Fuse. Επιβεβαίωση;`
    );
    if (!confirmed) return;

    setIssuingId(row.id);
    try {
      const { data } = await doctorInvoicesAPI.issue({
        doctorId: row.id, year, month,
      });
      const mark = data?.data?.elorus?.mark;
      toast.success(`✅ Εκδόθηκε · MARK ${mark || '—'}`);
      await load();
    } catch (err) {
      console.error('issue error:', err);
      const msg = err?.response?.data?.message
               || err?.response?.data?.error
               || 'Σφάλμα έκδοσης';
      toast.error(msg);
    } finally {
      setIssuingId(null);
    }
  };

  const totalGross = rows.reduce((sum, r) => sum + (Number(r.gross_cents) || 0), 0);
  const totalCount = rows.reduce((sum, r) => sum + (Number(r.consult_count) || 0), 0);
  const eligibleDoctors = rows.filter((r) => r.consult_count > 0).length;
  const issuedCount = rows.filter((r) => r.invoice_status === 'issued').length;

  return (
    <>
      <PageHeader
        title="Τιμολόγια Ιατρών"
        subtitle="Μηνιαία τιμολόγια προμήθειας πλατφόρμας (2.1)"
      />

      <Card title="Επιλογή Περιόδου">
        <div style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>
            <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 6 }}>Μήνας:</span>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                    style={{ padding: '6px 10px', borderRadius: 8 }}>
              {MONTHS_EL.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 6 }}>Έτος:</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                    style={{ padding: '6px 10px', borderRadius: 8 }}>
              {[year - 1, year, year + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <ActionBtn onClick={load} disabled={loading}>
            {loading ? 'Φόρτωση...' : 'Ανανέωση'}
          </ActionBtn>
        </div>
      </Card>

      <Card title={`Ιατροί — ${MONTHS_EL[month - 1]} ${year}`}>
        <div style={{ padding: 12 }}>
          {loading && <div style={{ color: 'var(--muted)', padding: 20 }}>Φόρτωση...</div>}
          {!loading && rows.length === 0 && (
            <div style={{ color: 'var(--muted)', padding: 20 }}>Δεν βρέθηκαν ιατροί.</div>
          )}
          {!loading && rows.length > 0 && (
            <>
              <div style={{
                display: 'flex', gap: 24, padding: '8px 4px 16px',
                fontSize: 12, color: 'var(--muted)', borderBottom: '1px solid var(--border)',
              }}>
                <div><strong>Επιλέξιμοι:</strong> {eligibleDoctors}</div>
                <div><strong>Εκδοθέντα:</strong> {issuedCount}</div>
                <div><strong>Σύνολο συνεδριών:</strong> {totalCount}</div>
                <div><strong>Σύνολο εσόδων:</strong> {fmtEuro(totalGross)}</div>
              </div>

              <table style={{
                width: '100%', borderCollapse: 'collapse', marginTop: 12,
                fontSize: 13,
              }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--muted)', fontWeight: 600 }}>
                    <th style={{ padding: '10px 8px' }}>Ιατρός</th>
                    <th style={{ padding: '10px 8px' }}>ΑΦΜ</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Συνεδρίες</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Καθαρό</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>ΦΠΑ 24%</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Σύνολο</th>
                    <th style={{ padding: '10px 8px' }}>Αρ.</th>
                    <th style={{ padding: '10px 8px' }}>Κατάσταση</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Ενέργεια</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const gross = Number(r.gross_cents) || 0;
                    const net   = Math.round(gross / 1.24);
                    const vat   = gross - net;
                    const missingBilling = !r.tax_id || !r.address_line || !r.city || !r.postal_code;
                    const eligible = r.consult_count > 0 && !missingBilling && !r.invoice_status;
                    const alreadyIssued = r.invoice_status === 'issued';

                    return (
                      <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</div>
                          {r.is_leaver && <div style={{ fontSize: 11, color: 'var(--muted)' }}>Λήξη συνεργασίας</div>}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          {r.tax_id || <span style={{ color: 'var(--danger)' }}>Λείπει</span>}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>{r.consult_count}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>{r.consult_count > 0 ? fmtEuro(net) : '—'}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>{r.consult_count > 0 ? fmtEuro(vat) : '—'}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>{r.consult_count > 0 ? fmtEuro(gross) : '—'}</td>
                        <td style={{ padding: '12px 8px' }}>{r.invoice_number || '—'}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <StatusBadge status={r.invoice_status} count={r.consult_count} />
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          {alreadyIssued && r.elorus_qr_url && (
                            <a href={r.elorus_qr_url} target="_blank" rel="noreferrer"
                               style={{ color: 'var(--magenta)', fontSize: 12 }}>
                              Προβολή →
                            </a>
                          )}
                          {eligible && (
                            <ActionBtn variant="primary" size="sm"
                                       onClick={() => handleIssue(r)}
                                       disabled={issuingId === r.id}>
                              {issuingId === r.id ? 'Έκδοση...' : 'Έκδοση'}
                            </ActionBtn>
                          )}
                          {r.consult_count > 0 && missingBilling && (
                            <span style={{ fontSize: 11, color: 'var(--danger)' }}>Λείπουν στοιχεία</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </Card>
    </>
  );
}
